import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './ConsumoBar.css';

/**
 * Página para Admin lançar consumos do bar
 */

const ConsumoBar = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rodadaAberta, setRodadaAberta] = useState(null);
  const [jogadoresConfirmados, setJogadoresConfirmados] = useState([]);
  const [todosJogadores, setTodosJogadores] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [jogadorSelecionado, setJogadorSelecionado] = useState('');
  const [produtoSelecionado, setProdutoSelecionado] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [consumos, setConsumos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarDados();
  }, [isAdmin, navigate]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      setError('');

      const [rodadasRes, produtosRes, jogadoresRes] = await Promise.all([
        apiClient.get('/rodadas'),
        apiClient.get('/produtos-bar'),
        apiClient.get('/jogadores'), // Buscar TODOS os jogadores cadastrados
      ]);

      const rodadas = Array.isArray(rodadasRes.data) ? rodadasRes.data : [];
      const aberta = rodadas.find(r => r.status === 'aberta');

      if (!aberta) {
        setError('Não há rodada aberta. Abra uma rodada primeiro.');
        setLoading(false);
        return;
      }

      setRodadaAberta(aberta);
      setProdutos(Array.isArray(produtosRes.data) ? produtosRes.data : []);

      // Carregar TODOS os jogadores cadastrados (não apenas confirmados)
      const todosJogadores = Array.isArray(jogadoresRes.data) ? jogadoresRes.data : [];
      setTodosJogadores(todosJogadores);

      // Carregar confirmações (para referência, mas não limitar seleção)
      const confirmacoesRes = await apiClient.get(`/rodadas/${aberta.id}/confirmacoes`);
      const confirmacoes = Array.isArray(confirmacoesRes.data) ? confirmacoesRes.data : [];
      setJogadoresConfirmados(confirmacoes);

      // Carregar consumos
      try {
        const consumosRes = await apiClient.get(`/consumos-bar/rodada/${aberta.id}`);
        setConsumos(Array.isArray(consumosRes.data) ? consumosRes.data : []);
      } catch (err) {
        // Se não houver consumos ainda, não é erro
        setConsumos([]);
      }

    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarConsumo = async (e) => {
    e.preventDefault();

    if (!jogadorSelecionado || !produtoSelecionado) {
      setError('Selecione o jogador e o produto');
      return;
    }

    try {
      setSalvando(true);
      setError('');

      await apiClient.post('/consumos-bar', {
        rodada_id: rodadaAberta.id,
        jogador_id: jogadorSelecionado,
        produto_id: produtoSelecionado,
        quantidade: parseInt(quantidade),
      });

      // Recarregar consumos para atualizar saldo_bar
      const consumosRes = await apiClient.get(`/consumos-bar/rodada/${rodadaAberta.id}`);
      setConsumos(Array.isArray(consumosRes.data) ? consumosRes.data : []);

      setJogadorSelecionado('');
      setProdutoSelecionado('');
      setQuantidade(1);
      
      // Mostrar mensagem de sucesso
      alert('Consumo adicionado com sucesso!');
      
      // Forçar recarregamento completo dos dados após um pequeno delay
      setTimeout(() => {
        carregarDados();
      }, 500);
    } catch (err) {
      console.error('Erro ao adicionar consumo:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar consumo');
    } finally {
      setSalvando(false);
    }
  };

  const handleRemoverConsumo = async (consumoId) => {
    if (!window.confirm('Deseja remover este consumo?')) {
      return;
    }

    try {
      await apiClient.delete(`/consumos-bar/${consumoId}`);
      
      // Recarregar consumos
      try {
        const consumosRes = await apiClient.get(`/consumos-bar/rodada/${rodadaAberta.id}`);
        setConsumos(Array.isArray(consumosRes.data) ? consumosRes.data : []);
      } catch (err) {
        setConsumos([]);
      }
      
      alert('Consumo removido com sucesso!');
    } catch (err) {
      console.error('Erro ao remover consumo:', err);
      setError(err.response?.data?.error || 'Erro ao remover consumo');
    }
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!rodadaAberta) {
    return (
      <div className="container">
        <div className="error">Não há rodada aberta. Abra uma rodada primeiro.</div>
        <button onClick={() => navigate('/rodadas')} className="btn btn-primary">
          Ir para Rodadas
        </button>
      </div>
    );
  }

  const produtoSelecionadoObj = produtos.find(p => p.id === produtoSelecionado);
  const totalEstimado = produtoSelecionadoObj
    ? (parseFloat(produtoSelecionadoObj.preco) * parseInt(quantidade)).toFixed(2)
    : '0.00';

  return (
    <div className="container consumo-bar-container">
      <div className="page-header">
        <h1>🍺 Consumo BAR</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      <div className="rodada-info">
        <p><strong>Rodada:</strong> {new Date(rodadaAberta.data).toLocaleDateString('pt-BR')}</p>
      </div>

      {error && <div className="error">{error}</div>}

      <form onSubmit={handleAdicionarConsumo} className="consumo-form">
        <div className="form-group">
          <label>Jogador *</label>
          <select
            value={jogadorSelecionado}
            onChange={(e) => setJogadorSelecionado(e.target.value)}
            required
          >
            <option value="">Selecione o jogador...</option>
            {todosJogadores.map(jogador => {
              // Verificar se está confirmado para mostrar indicador
              const estaConfirmado = jogadoresConfirmados.some(c => 
                (c.jogador_id || c.jogador?.id) === jogador.id
              );
              return (
                <option key={jogador.id} value={jogador.id}>
                  {jogador.nome} {estaConfirmado ? '✓' : ''}
                </option>
              );
            })}
          </select>
          <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
            💡 Você pode selecionar qualquer jogador cadastrado, não apenas os confirmados na rodada.
            {jogadoresConfirmados.length > 0 && (
              <span> (✓ = Confirmado na rodada)</span>
            )}
          </small>
        </div>

        <div className="form-group">
          <label>Produto *</label>
          <select
            value={produtoSelecionado}
            onChange={(e) => setProdutoSelecionado(e.target.value)}
            required
          >
            <option value="">Selecione o produto...</option>
                {produtos.length > 0 ? (
              produtos.map(produto => (
                <option key={produto.id} value={produto.id}>
                  {produto.nome} - R$ {parseFloat(produto.preco).toFixed(2)}
                </option>
              ))
            ) : (
              <option value="" disabled>Nenhum produto cadastrado</option>
            )}
          </select>
          {produtos.length === 0 && (
            <small style={{ color: '#f44336' }}>
              ⚠️ Nenhum produto cadastrado. Cadastre produtos primeiro.
            </small>
          )}
        </div>

        <div className="form-group">
          <label>Quantidade *</label>
          <input
            type="number"
            min="1"
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            required
          />
        </div>

        {produtoSelecionado && (
          <div className="total-estimado">
            <strong>Total: R$ {totalEstimado}</strong>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" disabled={salvando} className="btn btn-primary">
            {salvando ? 'Adicionando...' : 'Adicionar Consumo'}
          </button>
        </div>
      </form>

      <div className="consumos-section">
        <h2>Consumos Lançados</h2>
        {consumos.length > 0 ? (
          <div className="consumos-list">
            {consumos.map(item => (
              <div key={item.jogador.id} className="consumo-jogador">
                <div className="consumo-header">
                  <strong>{item.jogador.nome}</strong>
                  <span className="saldo-bar">Saldo BAR: R$ {parseFloat(item.saldo_bar || 0).toFixed(2)}</span>
                </div>
                {item.consumos && item.consumos.length > 0 && (
                  <div className="consumos-detalhes">
                    {item.consumos.map(consumo => (
                      <div key={consumo.id} className="consumo-item">
                        <span>{consumo.produto?.nome || 'Produto'}</span>
                        <span>Qtd: {consumo.quantidade}</span>
                        <span>R$ {parseFloat(consumo.total).toFixed(2)}</span>
                        <button
                          onClick={() => handleRemoverConsumo(consumo.id)}
                          className="btn btn-danger btn-xs"
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="sem-consumos">Nenhum consumo lançado ainda.</p>
        )}
      </div>
    </div>
  );
};

export default ConsumoBar;
